import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin-dashboard', { replace: true });
      } else {
        navigate('/employee-dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Input validation
    if (!email || !password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        'https://api.swasthmitra.com/api/usermedical/login',
        { email, password },
        {
          timeout: 10000, // 10 second timeout
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.token && response.data.user) {
        // Use auth context for login
        const loginSuccess = login(response.data.user, response.data.token);
        
        if (loginSuccess) {
          // Redirect based on user role
          if (response.data.user.role === 'admin') {
            navigate('/admin-dashboard', { replace: true });
          } else {
            navigate('/employee-dashboard', { replace: true });
          }
        } else {
          setError('Failed to save login information. Please try again.');
        }
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'ECONNABORTED') {
        setError('Request timeout. Please check your internet connection.');
      } else if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 'Invalid credentials';
        setError(errorMessage);
      } else if (error.request) {
        // Network error
        setError('Network error. Please check your internet connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="custom-login-container">
      <div className="custom-login-box">
        <h2 className="custom-login-title">Admin Dashboard Login</h2>
        {error && (
          <div className="custom-error-message" style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="custom-form-group">
            <label htmlFor="email" className="custom-label">Email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="custom-input"
              placeholder="Enter your email address"
            />
          </div>
          <div className="custom-form-group">
            <label htmlFor="password" className="custom-label">Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="custom-input"
              placeholder="Enter your password"
            />
          </div>
          <button 
            type="submit" 
            className="custom-btn"
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              position: 'relative'
            }}
          >
            {isLoading ? (
              <>
                <span style={{ marginRight: '10px' }}>Logging in...</span>
                <div style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
        
        {/* Loading spinner animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Login;
